'use client';

import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  CreditCard,
  ScrollText,
  Scale,
  LifeBuoy,
  Hash,
  Server,
  Menu,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { canAccessAdmin } from '@/lib/auth-routing';
import { AppThemeToggle } from '@/components/app-theme-toggle';
import { CommandBar } from '@/components/premium-ui';
import { cn, Logo, IconButton } from '@mpange/ui';

const adminNav = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { href: '/admin/organizations', label: 'Organizations', icon: Building2 },
  { href: '/admin/users', label: 'Users', icon: Users },
  { href: '/admin/kyc', label: 'KYC', icon: Shield },
  { href: '/admin/contracts', label: 'Contracts', icon: ScrollText },
  { href: '/admin/payments', label: 'Payments', icon: CreditCard },
  { href: '/admin/reconciliation', label: 'Reconciliation', icon: Scale },
  { href: '/admin/sender-ids', label: 'Sender IDs', icon: Hash },
  { href: '/admin/support', label: 'Support', icon: LifeBuoy },
  { href: '/admin/providers', label: 'Providers', icon: Server },
  { href: '/admin/audit', label: 'Audit log', icon: ScrollText },
];

export function AdminShell({ children }: { children: React.ReactNode }) {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/login?redirect=/admin', { replace: true });
      return;
    }

    if (!canAccessAdmin(user)) {
      if (!user.emailVerified || user.status === 'pending_verification') {
        navigate('/verify-email/pending', { replace: true });
      }
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!canAccessAdmin(user)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="max-w-md text-center">
          <p className="text-sm font-medium text-danger">Staff access required</p>
          <p className="mt-2 text-sm text-text-muted">This area is restricted to MPANGE platform staff.</p>
          <button
            type="button"
            onClick={() => {
              void logout();
              navigate('/login');
            }}
            className="mt-4 text-sm text-brand hover:underline"
          >
            Sign in with a staff account
          </button>
        </div>
      </div>
    );
  }

  const sidebar = (
    <aside className="flex h-full w-[240px] shrink-0 flex-col border-r border-border bg-surface">
      <div className="flex h-14 items-center border-b border-border px-4">
        <Logo size="sm" variant="mark" />
        <span className="ml-2 text-xs font-medium uppercase tracking-wide text-text-muted">
          Admin
        </span>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {adminNav.map((item) => {
          const active = item.exact
            ? pathname === item.href
            : pathname === item.href || pathname.startsWith(`${item.href}/`);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileOpen(false)}
              className={cn(
                'flex items-center gap-2.5 rounded-md-token px-2.5 py-2 text-[13px] transition-colors transition-fast',
                active
                  ? 'bg-brand-soft font-medium text-brand'
                  : 'text-text-muted hover:bg-surface-muted hover:text-text',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-border p-3">
        <button
          type="button"
          onClick={() => {
            void logout();
            navigate('/login');
          }}
          className="flex w-full items-center gap-2 rounded-md-token px-2.5 py-2 text-[13px] text-text-muted hover:bg-surface-muted"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-bg">
      <div className="hidden md:block">{sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full shadow-modal">{sidebar}</div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-surface px-4 md:px-6">
          <div className="flex items-center gap-2">
            <IconButton label="Open navigation" className="md:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="h-4 w-4" />
            </IconButton>
            <CommandBar placeholder="Search organizations, routes, tickets..." />
            <span className="hidden text-xs font-medium uppercase tracking-[0.14em] text-text-muted lg:block">Platform operations</span>
          </div>
          <div className="flex items-center gap-2">
            <AppThemeToggle />
            <span className="hidden h-6 w-px bg-border sm:block" aria-hidden />
            <div className="hidden items-center gap-2 text-xs text-text-muted sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
              All systems operational
            </div>
          </div>
        </header>
        <main className="flex-1 surface-noise">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
